import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Sprint } from "@/services/sprint.service";
import { STATUS_BADGE, formatSprintRange } from "./sprint-utils";
import { SprintActions, type SprintRowHandlers } from "./SprintActions";
import type { SprintSort, SprintSortKey } from "./useSprintsFilter";

const TH = "px-4 py-2.5 text-left text-xs font-medium text-muted";

function SortHeader({
	label,
	sortKey,
	sort,
	onSort,
}: {
	label: string;
	sortKey: SprintSortKey;
	sort: SprintSort;
	onSort: (key: SprintSortKey) => void;
}) {
	const active = sort.key === sortKey;
	const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
	return (
		<th
			className={TH}
			aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
		>
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className={`inline-flex items-center gap-1 transition-colors hover:text-foreground cursor-pointer ${active ? "text-foreground" : ""}`}
			>
				{label}
				<Icon className={`h-3 w-3 ${active ? "" : "opacity-50"}`} />
			</button>
		</th>
	);
}

export function SprintsTable({
	sprints,
	sort,
	onSort,
	...handlers
}: SprintRowHandlers & {
	sprints: Sprint[];
	sort: SprintSort;
	onSort: (key: SprintSortKey) => void;
}) {
	const navigate = useNavigate();

	return (
		<div className="overflow-hidden rounded-lg border border-border bg-surface">
			<div className="overflow-x-auto">
				<table className="w-full min-w-[720px] text-sm">
					<thead>
						<tr className="border-b border-border bg-muted-subtle">
							<SortHeader label="Name" sortKey="name" sort={sort} onSort={onSort} />
							<SortHeader label="Dates" sortKey="start_date" sort={sort} onSort={onSort} />
							<SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
							<th className={TH}>Created by</th>
							<th className={`${TH} text-right`}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{sprints.map((sprint) => {
							const { variant, label } = STATUS_BADGE[sprint.status];
							return (
								<tr key={sprint.id} className="border-b border-border last:border-0 hover:bg-muted-subtle/50">
									<td className="px-4 py-3">
										<button
											type="button"
											onClick={() => navigate(`/sprints/${sprint.id}`)}
											className="font-medium text-foreground hover:text-primary transition-colors cursor-pointer"
										>
											{sprint.name}
										</button>
									</td>
									<td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
										{formatSprintRange(sprint.start_date, sprint.end_date)}
									</td>
									<td className="px-4 py-3">
										<Badge variant={variant}>{label}</Badge>
									</td>
									<td className="px-4 py-3 text-muted-foreground">
										{sprint.created_by?.full_name ?? "-"}
									</td>
									<td className="px-4 py-2">
										<div className="flex justify-end">
											<SprintActions sprint={sprint} {...handlers} />
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
